/**
 * @file Root component and provider stack.
 *
 * Provider order matters:
 *   QueryClientProvider — server-state cache for the API calls.
 *   WebEnvProvider      — the validated build-time env, injected so components
 *                         (and tests) never read `import.meta.env` directly.
 *   AuthRoot            — calls the chosen provider's `useSession` once and
 *                         publishes the result; see auth/context.tsx.
 *   RouterProvider      — the routes. Inside AuthRoot because the root layout
 *                         renders the sign-in screen when signed out, and inside
 *                         QueryClientProvider because every workspace-scoped
 *                         route resolves its workspace from `GET /me`.
 *
 * The QueryClient is created once per mount via a lazy `useState` initialiser.
 * A module-level singleton would leak cached responses between tests, and more
 * importantly between user sessions in a long-lived tab.
 */
import { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { AuthRoot } from "./auth/context";
import { betterWebAuth } from "./auth/better";
import { WebEnvProvider } from "./env-context";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { createAppRouter } from "./router";
import type { WebEnv } from "./env";

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // The /me payload is an authorization map: a membership can be revoked
        // server-side, so it must not be treated as fresh indefinitely.
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: true,
      },
      mutations: {
        // Never auto-retry a create: a retried POST could double-apply. The
        // server is idempotent where it matters (owner membership upsert), but
        // the client does not get to assume that.
        retry: 0,
      },
    },
  });
}

export function App({ env }: { readonly env: WebEnv }) {
  const [queryClient] = useState(createQueryClient);
  // One router per mount, for the same reason as the QueryClient.
  const [router] = useState(() => createAppRouter());

  // One auth provider, locally and in production: Better Auth against the same API.
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <WebEnvProvider value={env}>
          <AuthRoot provider={betterWebAuth}>
            <RouterProvider router={router} />
          </AuthRoot>
        </WebEnvProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
